import {observer} from 'mobx-react-lite'
import React, {useEffect, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {
  DataObjectId,
  Department,
  getParentDepartments,
  isDepartmentInsideOf
} from 'yougile-model'
import {DepartmentsClient} from '@client/departments-client'
import {Sync} from '@client/sync'
import {LinkButton} from '@components/common/base/buttons'
import {ShowMore} from '@components/dashboard/common/show-more'
import {MessageBox} from '@components/message-box'
import {useSyncEvent} from '@containers/hooks'
import {DragControl, useDraggable} from '@containers/hooks-draggable'
import {globals} from '@extensions/globals-base'
import {isSafari} from '@extensions/platform'
import {DepartmentEditorCall} from './department-editor-call'
import {DepartmentItem} from './department-item'
import {
  DepartmentsEditContext,
  DepartmentsEditStore,
  useDepartmentsEditStore
} from './departments-edit-store'
import {downloadRolesInDepartment} from './download-roles-in-department'

const dragImage = {
  img: undefined as unknown as HTMLImageElement,
  init() {
    if (!dragImage.img) {
      dragImage.img = isSafari()
        ? (() => {
            const el = document.createElement('img')
            document.body.appendChild(el)
            return el
          })()
        : new Image()
      dragImage.img.src = '/img/projects/drag-dept.svg'
    }
  }
}

export const DepartmentsEdit = () => {
  const [store] = useState(() => new DepartmentsEditStore())

  return (
    <DepartmentsEditContext.Provider value={store}>
      <DepartmentsEditBase />
    </DepartmentsEditContext.Provider>
  )
}

const drag = (
  draggedId: DataObjectId | 'top',
  to: DataObjectId | 'top',
  departments: Department[]
) => {
  const item = departments.find(d => d.id === draggedId)
  const dest = to === 'top' ? 'top' : departments.find(d => d.id === to)
  if (!item || !dest) {
    return
  }
  if (to === draggedId) {
    return
  }
  const box = Sync.getCurrentSandbox()
  if (dest === 'top') {
    item.parentId = undefined
    DepartmentsClient.updateDepartment(box, item)
  } else {
    if (isDepartmentInsideOf(dest, item, departments)) {
      MessageBox.warn(Lang.msg.companies.departmentDisallowParentInsideChild)
      return
    }
    item.parentId = dest.id
    DepartmentsClient.updateDepartment(box, item)
  }
}

const TwDepartmentEditorCall = tw.div`
  flex
  justify-between
  items-center
  h-40
`

const DepartmentsEditBase = observer(() => {
  const [state, store] = useDepartmentsEditStore()

  useEffect(() => {
    store.calculate()
  }, [])

  useEffect(() => {
    store.calculateFiltered()
  }, [state.filterQuery])

  useSyncEvent(
    'current',
    {
      debounceTime: 100,
      filter: '*'
    },
    () => {
      store.calculateFiltered()
      store.calculate()
    }
  )

  const [edited, setEdited] = React.useState<DataObjectId | 'new'>()

  const click = (id: DataObjectId) => () => setEdited(id)

  const calculatedDepartments = state.departments.slice(0, state.maxShow)

  dragImage.init()

  const dragCtl = useDraggable<DataObjectId | 'top'>({
    onDrop: (draggedId, to) => drag(draggedId, to, state.departments),
    ghost: dragImage.img
  })

  const draggedDep = dragCtl.draggedId
    ? calculatedDepartments.find(d => d.id === dragCtl.draggedId)
    : undefined

  const draggedNotTop = !!draggedDep && !!draggedDep.parentId

  return (
    <div className="flex flex-col gap-8">
      {draggedNotTop && state.isAdmin ? (
        <DepartmentToTopLevel dragCtl={dragCtl} />
      ) : (
        <TwDepartmentEditorCall>
          <DepartmentEditorCall edited={edited} setEdited={setEdited} />
          <LinkButton icon="IconDownload" onClick={downloadRolesInDepartment}>
            {Lang.msg.users.downloadXlsxList}
          </LinkButton>
        </TwDepartmentEditorCall>
      )}
      <DepartmentItems click={click} dragCtl={dragCtl} />
      <ShowMoreDepartments />
    </div>
  )
})

const TwDepartmentToTopLevel = tw.div<{$over: boolean}>`
  rounded-2
  border
  border-dashed
  border-grey-l60
  h-40
  text-secondary
  ${p => (p.$over ? 'bg-grey-l80' : '')}
`

const TwDepartmentToTopLevelText = tw.div`
  flex
  items-center
  justify-center
  text-14
  h-full
  pl-32
  text-secondary
`

const DepartmentToTopLevel = observer(
  ({dragCtl}: {dragCtl: DragControl<DataObjectId | 'top'>}) => {
    return (
      <TwDepartmentToTopLevel
        onDragEnter={dragCtl.dragEnter('top')}
        onDrop={dragCtl.drop('top')}
        onDragLeave={dragCtl.dragLeave('top')}
        $over={dragCtl.dragOverId === 'top'}
      >
        <TwDepartmentToTopLevelText>
          {Lang.msg.companies.departmentToTopLevel}
        </TwDepartmentToTopLevelText>
      </TwDepartmentToTopLevel>
    )
  }
)

const TwDepartmentItem = tw.div`
  flex
  flex-col
  gap-8
`

const DepartmentItems = observer(
  ({
    dragCtl,
    click
  }: {
    dragCtl: DragControl<DataObjectId | 'top'>
    click: (id: DataObjectId) => () => void
  }) => {
    const [state] = useDepartmentsEditStore()
    const calculatedDepartments = state.departments.slice(0, state.maxShow)
    const items = state.filterQuery
      ? state.filtered
      : calculatedDepartments.filter(d => !d.parentId)

    const userId = globals.currentUserId

    const canEdit = (dep: Department) =>
      state.isAdmin ||
      (dep &&
        (dep.managers.includes(userId) ||
          !!getParentDepartments(dep, state.departments).find(d =>
            d?.managers?.includes(userId)
          )))

    if (items.length === 0) {
      return null
    }

    return (
      <TwDepartmentItem>
        {items.map((d, ix) => (
          <DepartmentItem
            key={d.id}
            department={d}
            all={calculatedDepartments}
            under={undefined}
            dragCtl={dragCtl}
            isAtEnd={ix === items.length - 1}
            click={click}
            canEdit={canEdit(d)}
            allUsers={state.allUsers}
            fromFilter={!!state.filterQuery}
          />
        ))}
      </TwDepartmentItem>
    )
  }
)

const ShowMoreDepartments = observer(() => {
  const [state, store] = useDepartmentsEditStore()
  if (state.filterQuery) {
    return null
  }
  return (
    <ShowMore
      list={state.departments}
      maxShow={state.maxShow}
      handler={() => store.showMore()}
    />
  )
})
